/**
 * Custom hooks per Batch OCR API
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost } from "@/lib/api-client";
import type {
    BatchOCRJobResponse,
    BatchOCRJobSummary,
    BatchOCRSubmitRequest,
    BatchOCRListFilters,
} from "@/types/api/batch-ocr";

const BATCH_OCR_QUERY_KEYS = {
    all: ["batch-ocr"] as const,
    lists: () => [...BATCH_OCR_QUERY_KEYS.all, "list"] as const,
    list: (filters?: BatchOCRListFilters) => [...BATCH_OCR_QUERY_KEYS.lists(), filters] as const,
    details: () => [...BATCH_OCR_QUERY_KEYS.all, "detail"] as const,
    detail: (batchJobId: string) => [...BATCH_OCR_QUERY_KEYS.details(), batchJobId] as const,
};

// Export query keys per invalidazione esterna se necessario
export { BATCH_OCR_QUERY_KEYS };

/**
 * Hook per sottomettere un nuovo batch OCR job
 */
export function useSubmitBatchOcr() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: BatchOCRSubmitRequest): Promise<BatchOCRJobResponse> => {
            return await apiPost<BatchOCRJobResponse>("/training/batch-ocr/submit", data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: BATCH_OCR_QUERY_KEYS.lists() });
        },
    });
}

/**
 * Hook per ottenere lo stato di un batch OCR job con polling condizionale
 * Il polling si ferma automaticamente quando il job raggiunge uno stato terminale
 */
export function useBatchOcrStatus(
    batchJobId: string | undefined,
    options?: {
        pollingEnabled?: boolean;
        pollingInterval?: number;
    },
) {
    const { pollingEnabled = true, pollingInterval = 7000 } = options || {};

    return useQuery({
        queryKey: BATCH_OCR_QUERY_KEYS.detail(batchJobId || ""),
        queryFn: async (): Promise<BatchOCRJobResponse> => {
            if (!batchJobId) {
                throw new Error("Batch job ID is required");
            }
            // Usa refresh=true per forzare aggiornamento da RunPod
            return await apiGet<BatchOCRJobResponse>(`/training/batch-ocr/${batchJobId}`, {
                params: { refresh: true },
            });
        },
        enabled: !!batchJobId,
        refetchInterval: (query) => {
            if (!pollingEnabled) return false;

            const data = query.state.data;
            if (!data) return pollingInterval;

            // Ferma polling per stati terminali
            const terminalStates = ["completed", "partially_completed", "failed"];
            if (terminalStates.includes(data.status)) {
                return false;
            }

            return pollingInterval;
        },
        staleTime: 0, // Sempre considerato stale durante polling
    });
}

/**
 * Hook per lista di tutti i batch OCR jobs con paginazione
 */
export function useBatchOcrList(
    filters?: BatchOCRListFilters,
    options?: Omit<import("@tanstack/react-query").UseQueryOptions<BatchOCRJobSummary[], Error>, "queryKey" | "queryFn">,
) {
    return useQuery({
        queryKey: BATCH_OCR_QUERY_KEYS.list(filters),
        queryFn: async (): Promise<BatchOCRJobSummary[]> => {
            return await apiGet<BatchOCRJobSummary[]>("/training/batch-ocr", {
                params: filters as Record<string, string | number | boolean | null | undefined> | undefined,
            });
        },
        ...options,
    });
}

/**
 * Hook per forzare refresh dello stato di un batch job
 */
export function useRefreshBatchOcr() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (batchJobId: string): Promise<BatchOCRJobResponse> => {
            return await apiPost<BatchOCRJobResponse>(`/training/batch-ocr/${batchJobId}/refresh`, {});
        },
        onSuccess: (_data, batchJobId) => {
            queryClient.invalidateQueries({ queryKey: BATCH_OCR_QUERY_KEYS.detail(batchJobId) });
            queryClient.invalidateQueries({ queryKey: BATCH_OCR_QUERY_KEYS.lists() });
        },
    });
}
